// Investor-side realtime listener for client portal responses.
// V1 parity: public/app/index.html L21253 checkPortalResponses() (V1 polls every
// 10 minutes via _portalCheckInterval at L19173-19175). V2 upgrades to a
// Firestore onSnapshot listener for true realtime surface.
//
// What it does for every portallink__* doc owned by the current uid:
//   - Detects status transitions pending → accepted / declined
//   - Delegates the estimate-side mutation (status + clientPortalStatus +
//     auto-promote approvalStatus if pending) to useEstimateMutations.changeStatus,
//     which in turn handles approvedAt/approvedBy/activityLog stamping when the
//     source is 'portal'.
//   - Adds the token to opts.portalUnreadTokens until investor acks it via
//     AlertBell — V1 writes to the same key (public/app/index.html L21303).
//   - Adds the token to opts.ackedPortalResponses so the same response doesn't
//     fire a double alert on reload (V1 public/app/index.html L21301).
//
// Template: useAssignmentsRealtime (same directory).

import { useEffect, useRef } from 'react'
import { onSnapshot, query, where } from 'firebase/firestore'
import { sharedCol } from '../lib/firebase/firestore'
import { useAuth } from './useAuth'
import { useAppState } from './useAppState'
import { useOptsMutation } from './useOptsMutation'
import { useEstimateMutations } from './useEstimateMutations'
import type { PortalLinkDoc } from '../types/firestore'

interface PortalDocLite {
  token: string
  ownerUid: string
  estId: number | undefined
  status: 'pending' | 'accepted' | 'declined' | undefined
  estName: string | undefined
  clientName: string | undefined
}

/**
 * Subscribe to shared/portallink__* docs owned by the current uid. Whenever a
 * portal doc transitions from pending → accepted/declined, fire the estimate
 * status mutation and mark the token as unread on the bell.
 *
 * No UI; mount in Shell.
 */
export function usePortalResponses(): void {
  const { effectiveUid: uid, isReady } = useAuth()
  const { data: state } = useAppState()
  const optsMut = useOptsMutation()
  const estMut = useEstimateMutations()

  // Track last-seen status per token so we only act on transitions, not echoes.
  const lastSeenRef = useRef<Map<string, 'pending' | 'accepted' | 'declined'>>(new Map())
  const initializedRef = useRef(false)

  useEffect(() => {
    if (!uid || !isReady) return

    const q = query(sharedCol(), where('ownerUid', '==', uid))

    const unsub = onSnapshot(
      q,
      (snap) => {
        const portalDocs: PortalDocLite[] = []
        snap.forEach((docSnap) => {
          if (!docSnap.id.startsWith('portallink__')) return
          const d = docSnap.data() as PortalLinkDoc & Partial<PortalDocLite>
          const token = docSnap.id.slice('portallink__'.length)
          portalDocs.push({
            token,
            ownerUid: d.ownerUid,
            estId: (d as unknown as { estId?: number }).estId,
            status: (d as unknown as { status?: PortalDocLite['status'] }).status,
            estName: (d as unknown as { estName?: string }).estName,
            clientName: (d as unknown as { clientName?: string }).clientName,
          })
        })

        // First snapshot: seed lastSeenRef without firing mutations. Without
        // this guard every existing 'accepted' doc would re-fire on page load.
        if (!initializedRef.current) {
          for (const p of portalDocs) {
            lastSeenRef.current.set(p.token, p.status || 'pending')
          }
          initializedRef.current = true
          return
        }

        // Grab latest state for transition checks / token list.
        const s = state
        if (!s) return
        const acked = new Set(s.opts.ackedPortalResponses || [])
        const unread = new Set(s.opts.portalUnreadTokens || [])
        let optsChanged = false

        for (const p of portalDocs) {
          const prev = lastSeenRef.current.get(p.token) || 'pending'
          const curr = p.status || 'pending'
          if (prev === curr) continue
          lastSeenRef.current.set(p.token, curr)

          if (curr !== 'accepted' && curr !== 'declined') continue
          if (p.estId == null) continue

          // Find the matching estimate. Skip when the portal doc belongs to a
          // deleted estimate — don't thrash writes on stale tokens.
          const est = s.estimates.find((e) => e.id === p.estId)
          if (!est) continue

          // Only fire the mutation when the estimate hasn't already caught up.
          // (Investor might have manually flipped status before the listener
          // saw the portal doc update.)
          if (est.status !== curr) {
            estMut.changeStatus.mutate(
              { id: p.estId, status: curr, source: 'portal' },
              {
                onError: (e) => {
                  console.warn('[portal] changeStatus failed for', p.token, e)
                },
              },
            )
          }

          // Bell unread. V1 L21301: push token to opts.portalUnreadTokens
          // unless investor already acked it.
          if (!acked.has(p.token)) {
            acked.add(p.token)
            unread.add(p.token)
            optsChanged = true
          }
        }

        if (optsChanged) {
          optsMut.mutate({
            ackedPortalResponses: Array.from(acked),
            portalUnreadTokens:   Array.from(unread),
          })
        }
      },
      (err) => {
        // Permissions or transient network. Listener errors are non-fatal —
        // V1 falls back to the 10-min poll on error (L21306).
        console.warn('[portal] responses listener error', err)
      },
    )

    return () => {
      unsub()
      // Reset so a sign-out / sign-in cycle re-seeds on the next mount.
      initializedRef.current = false
      lastSeenRef.current.clear()
    }
    // Intentionally scoped to uid/isReady only. Referencing state in deps would
    // re-subscribe every cache update and lose lastSeenRef continuity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, isReady])
}
